import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckboxButton, Dropdown, Title } from '../../components/shared';
import { selectLang, selectTheme } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { setThemeMode, showFeedback } from '../../store/ui/ui.actions';
import AccountBrand from './AccountBrand';
import { selectProfile, selectSettings } from '../../store/auth/auth.selector';
import { authLoadMe, updateUserSetting } from '../../store/auth/auth.actions';
import { sendSingleRequest } from '../../apis';

const getUtcOffsetLabel = (tz: string): string => {
  const now = new Date();
  const utcMs = new Date(
    now.toLocaleString('en-US', { timeZone: 'UTC' }),
  ).getTime();
  const tzMs = new Date(
    now.toLocaleString('en-US', { timeZone: tz }),
  ).getTime();
  const offsetMin = Math.round((tzMs - utcMs) / 60000);
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const h = String(Math.floor(abs / 60)).padStart(2, '0');
  const m = String(abs % 60).padStart(2, '0');
  return `(UTC ${sign}${h}:${m})`;
};

const TIMEZONE_OPTIONS = [
  { value: 'Pacific/Auckland', name: 'Auckland, New Zealand' },
  { value: 'Pacific/Chatham', name: 'Chatham Islands' },
  { value: 'Pacific/Fiji', name: 'Fiji' },
  { value: 'Australia/Sydney', name: 'Sydney, New South Wales' },
  { value: 'Australia/Adelaide', name: 'Adelaide, South Australia' },
  { value: 'Asia/Tokyo', name: 'Tokyo, Japan' },
  { value: 'Asia/Seoul', name: 'Seoul, South Korea' },
  { value: 'Australia/Perth', name: 'Perth, Western Australia' },
  { value: 'Asia/Singapore', name: 'Singapore' },
  { value: 'Asia/Bangkok', name: 'Bangkok, Thailand' },
  { value: 'Asia/Dhaka', name: 'Dhaka, Bangladesh' },
  { value: 'Asia/Kolkata', name: 'Kolkata, India' },
  { value: 'Asia/Dubai', name: 'Dubai, UAE' },
  { value: 'Europe/Moscow', name: 'Moscow, Russia' },
  { value: 'Europe/Helsinki', name: 'Helsinki, Eastern Europe' },
  { value: 'Europe/Paris', name: 'Paris, Central Europe' },
  { value: 'Europe/London', name: 'London, UK' },
  { value: 'Atlantic/Azores', name: 'Azores, Portugal' },
  { value: 'America/Sao_Paulo', name: 'São Paulo, Brazil' },
  { value: 'America/Argentina/Buenos_Aires', name: 'Buenos Aires, Argentina' },
  { value: 'America/St_Johns', name: "St. John's, Newfoundland" },
  { value: 'America/Halifax', name: 'Halifax, Atlantic Time' },
  { value: 'America/New_York', name: 'New York, Eastern Time' },
  { value: 'America/Chicago', name: 'Chicago, Central Time' },
  { value: 'America/Denver', name: 'Denver, Mountain Time' },
  { value: 'America/Los_Angeles', name: 'Los Angeles, Pacific Time' },
  { value: 'Pacific/Honolulu', name: 'Honolulu, Hawaii' },
  { value: 'Pacific/Tahiti', name: 'Tahiti, French Polynesia' },
].map(tz => ({
  id: tz.value,
  value: tz.value,
  label: `${getUtcOffsetLabel(tz.value)} ${tz.name}`,
}));

const AppearancePage = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const theme = useSelector(selectTheme);
  const profile = useSelector(selectProfile);
  const hideHomeChats = useSelector(selectSettings).hide_home_chats;

  const [disabled, setDisabled] = useState(false);
  const [timezone, setTimezone] = useState(
    profile?.account_brand?.timezone ?? '',
  );

  const updateLanguage = async (v: string) => {
    await dispatch(updateUserSetting('lang', v));
    await dispatch(authLoadMe(true));
  };

  const updateTheme = (v: any) => dispatch(setThemeMode(v));

  const updateTimezone = async (v: string) => {
    setTimezone(v);
    setDisabled(true);
    const res = await sendSingleRequest(
      { timezone: v },
      'POST',
      'api/account/brand',
      true,
    );
    if (!res.status) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, 'Server error'),
        }),
      );
      setTimezone(profile?.account_brand?.timezone ?? '');
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, 'Updated successfully'),
        }),
      );
      await dispatch(authLoadMe(true));
    }
    setDisabled(false);
  };

  const updateDisplayChats = async (v: boolean) => {
    setDisabled(true);
    await dispatch(updateUserSetting('hide_home_chats', v));
    setDisabled(false);
  };

  return (
    <div className='content pb-32'>
      <Title
        className='mb-24'
        size={5}
        color='black-3'
        align='default'
        fontWeight={600}
      >
        {translate(lang, 'Appearance')}
      </Title>
      <div className='pt-24 mb-32'>
        <Dropdown
          label={translate(lang, 'Language')}
          options={[
            { id: 'en', value: 'en', label: 'English' },
            { id: 'fr', value: 'fr', label: 'Français' },
            { id: 'es', value: 'es', label: 'Español' },
          ]}
          value={lang ?? 'en'}
          onChange={updateLanguage}
        />
      </div>
      <div className='mb-32'>
        <Dropdown
          label={translate(lang, 'Time zone')}
          options={TIMEZONE_OPTIONS}
          value={timezone}
          onChange={updateTimezone}
          disabled={disabled}
        />
      </div>
      <div className='mb-32'>
        <Dropdown
          label={translate(lang, 'Theme')}
          options={[
            {
              id: 'default',
              value: 'default',
              label: translate(lang, 'Default'),
            },
            { id: 'dark', value: 'dark', label: translate(lang, 'Dark') },
          ]}
          value={theme ?? 'default'}
          onChange={v => updateTheme(v)}
        />
      </div>
      <div className='mb-64'>
        <div className='mb-17'>
          <Title size={6} color='black-3' align='default' fontWeight={500}>
            {translate(lang, 'Display options')}
          </Title>
        </div>
        <CheckboxButton
          label={translate(lang, '_hide_recent_chats_dashboard')}
          checked={hideHomeChats || false}
          onChange={e => updateDisplayChats(e.target.checked)}
          disabled={disabled}
        />
      </div>
      <AccountBrand />
    </div>
  );
};

export default AppearancePage;
